/**
 * @spec docs/specs/features/staking-position-ui.md
 * Formatting utilities for numbers, dates, and addresses
 */
import { formatUnits, getAddress } from 'ethers';
import { DECIMALS } from './constants';

/**
 * Format token amount with proper decimals
 */
export function formatTokenAmount(
  amount: bigint | string,
  decimals: number = DECIMALS.PHNM,
  options?: {
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
  }
): string {
  const formatted = formatUnits(amount, decimals);
  const num = parseFloat(formatted);

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
  }).format(num);
}

/**
 * Format PHNM token amount
 */
export function formatPHNM(amount: bigint | string): string {
  return formatTokenAmount(amount, DECIMALS.PHNM);
}

/**
 * Format USDT amount
 */
export function formatUSDT(amount: bigint | string): string {
  return formatTokenAmount(amount, DECIMALS.USDT);
}

/**
 * Format USDC amount (6 decimals)
 */
export function formatUSDC(amount: bigint | string): string {
  return formatTokenAmount(amount, 6);
}

/**
 * Format large numbers with suffixes (K, M, B)
 */
export function formatCompact(value: number): string {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toFixed(0);
}

/**
 * Format Ethereum address with EIP-55 checksum (truncate middle)
 */
export function formatAddress(address: string, chars: number = 4): string {
  if (!address) return '';

  // Apply EIP-55 checksum
  let checksummed: string;
  try {
    checksummed = getAddress(address);
  } catch {
    // If invalid address, return as-is
    checksummed = address;
  }

  if (checksummed.length <= chars * 2 + 2) return checksummed;
  return `${checksummed.slice(0, chars + 2)}...${checksummed.slice(-chars)}`;
}

/**
 * Format date relative to now (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelativeTime(timestamp: number | Date): string {
  const now = Date.now();
  const then = typeof timestamp === 'number' ? timestamp * 1000 : timestamp.getTime();
  const diffMs = then - now;
  const diffSec = Math.abs(Math.floor(diffMs / 1000));

  const units = [
    { name: 'year', seconds: 31536000 },
    { name: 'month', seconds: 2592000 },
    { name: 'day', seconds: 86400 },
    { name: 'hour', seconds: 3600 },
    { name: 'minute', seconds: 60 },
    { name: 'second', seconds: 1 },
  ];

  for (const unit of units) {
    const count = Math.floor(diffSec / unit.seconds);
    if (count >= 1) {
      const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
      return rtf.format(diffMs > 0 ? count : -count, unit.name as Intl.RelativeTimeFormatUnit);
    }
  }

  return 'just now';
}

/**
 * Format absolute date (e.g., "Jan 14, 2026")
 */
export function formatDate(timestamp: number | Date): string {
  const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : timestamp;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Format date and time (e.g., "Jan 14, 2026, 3:45 PM")
 */
export function formatDateTime(timestamp: number | Date): string {
  const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : timestamp;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format duration (e.g., "2h 30m", "3d 5h")
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 && days === 0) parts.push(`${minutes}m`);

  return parts.slice(0, 2).join(' ') || '0m';
}

/**
 * Split a USDT amount into integer and decimal parts for split-size display.
 * e.g. 458910000n (6 dec) → { integer: "458", decimal: ".91" }
 * Returns { integer: "0", decimal: "" } for zero amounts.
 */
export function splitUSDTAmount(amount: bigint | string): {
  integer: string;
  decimal: string;
} {
  const formatted = formatUSDT(amount);
  const dotIndex = formatted.indexOf('.');
  if (dotIndex === -1) {
    return { integer: formatted, decimal: '' };
  }
  return {
    integer: formatted.slice(0, dotIndex),
    decimal: formatted.slice(dotIndex),
  };
}

/**
 * Format percentage
 */
export function formatPercent(value: number, decimals: number = 2): string {
  return `${value.toFixed(decimals)}%`;
}
